import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { AreaOfUseService } from '../../../../core/catalog';
import { Feed, FeedBadgeType } from '../../../../core/model';
import { FeedFacade, PrincipalConfigurationService, UserFacade } from '../../../../core/user';
import { UserUtilService } from '../../../../core/user/services/user-util.service';
import { MenuSiteContextMode } from '../../cms-libs/menu-site-context';
import { NavigationNode } from '../../cms-libs/navigation/navigation-node.model';
import { MobileNavigationStateService, State } from '../../services/mobile-navigation-state.service';

@Component({
  selector: 'py-mobile-navigation-links',
  templateUrl: './mobile-navigation-links.component.html',
  styleUrls: ['./mobile-navigation-links.component.scss'],
})
export class MobileNavigationLinksComponent implements OnInit, OnDestroy {
  readonly directServicesLinkStyleClass = 'direct-services-link';
  @Input() navigationNode: NavigationNode;

  isLoggedIn$: Observable<boolean> = this.authService.isUserLoggedIn();
  notificationCount$: Observable<number>;
  pollingSubs: Subscription;
  subscriptions = new Subscription();
  feedData$: Observable<Feed>;
  userFirstName$: Observable<string>;
  shouldDisplayUserFirstname$: Observable<boolean>;

  menuSiteContextModes: typeof MenuSiteContextMode = MenuSiteContextMode;

  constructor(
    private authService: AuthService,
    private mobileNavigationStateService: MobileNavigationStateService,
    private feedService: FeedFacade,
    private areaOfUseService: AreaOfUseService,
    private principalConfigurationService: PrincipalConfigurationService,
    private userService: UserFacade,
    private userUtilService: UserUtilService
  ) {}

  ngOnInit() {
    this.feedData$ = this.feedService.getFeedData().pipe(shareReplay({ bufferSize: 1, refCount: false }));

    this.userFirstName$ = this.userService.get().pipe(
      filter((user) => user && !!Object.keys(user).length),
      map((user) => user.firstName)
    );

    this.shouldDisplayUserFirstname$ = this.principalConfigurationService.isEnabled('displayUserFirstnameInHeader');

    this.initNotifcationCountObservables();
    this.initAreaOfUseRefreshObservable();
  }

  handleMenuListItemClick(item) {
    this.mobileNavigationStateService.addPrevItem({
      title: this.mobileNavigationStateService.getDrawerTitle,
      subItems: [],
      state: State.Menu,
    });

    this.mobileNavigationStateService.setDrawerTitle(item.title);

    if (item.uid === 'header_products_link') {
      this.mobileNavigationStateService.setCurrentState(State.ProductsMenu);
    } else if (item.uid === 'header_services_link') {
      this.mobileNavigationStateService.setCurrentState(State.ServicesMenu);
    } else if (item.uid === 'header_area_of_use_link') {
      this.mobileNavigationStateService.setCurrentState(State.AreaOfUseMenu);
    } else {
      this.mobileNavigationStateService.setCurrentState(State.SubMenu);
    }
  }

  initNotifcationCountObservables() {
    this.notificationCount$ = this.feedData$.pipe(
      filter((feedData) => !!feedData),
      map((feedData) => {
        let notificationCount = 0;
        notificationCount += this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.PENDING_APPROVAL_ORDERS);
        notificationCount += this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.RETURNED_ORDERS);
        notificationCount += this.userUtilService.getCountForBadgeType(
          feedData.badges,
          FeedBadgeType.SHOPPING_LISTS_WITH_UNAVAILABLE_ARTICLES_COUNT
        );
        notificationCount += this.userUtilService.getCountForBadgeType(
          feedData.badges,
          FeedBadgeType.ORDER_CARDS_WITH_UNAVAILABLE_PRODUCTS_COUNT
        );
        notificationCount += this.userUtilService.getCountForBadgeType(
          feedData.badges,
          FeedBadgeType.AREAS_OF_USE_UNAVAILABLE_PRODUCTS_COUNT
        );
        notificationCount += this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.NEW_USERS_COUNT);
        notificationCount += this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.LOCKED_OUT_USERS_COUNT);
        return notificationCount;
      })
    );
  }

  initAreaOfUseRefreshObservable() {
    this.subscriptions.add(
      this.feedData$
        .pipe(
          takeUntil(this.principalConfigurationService.isEnabled('enableDisplayAreaOfUse').pipe(filter((val) => !val))), //terminating subscription if config is false
          this.areaOfUseService.shouldRefreshAreasOfUseOperator()
        )
        .subscribe((shouldRefreshAreasOfUse) => {
          if (shouldRefreshAreasOfUse) {
            this.areaOfUseService.reloadAreasOfUse();
          }
        })
    );
  }

  ngOnDestroy(): void {
    if (this.pollingSubs) {
      this.pollingSubs.unsubscribe();
    }
    this.subscriptions.unsubscribe();
  }
}
